import React, { useState, useCallback, useMemo } from 'react'
import { func, object, bool } from 'prop-types'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { ScrollView, View, Platform } from 'react-native'
import { LocationsSelectInput, EquipmentsSelectInput, Button, Select, Modal, ConfirmationDialog, KeyboardView, Divider } from '../../../../components'
import { setMyDriver } from '../../../../redux/tractors/myDriversActions'
import { formatDriver } from '../../helpers'
import useStyles from './useStyles'
import { usePlatforms } from '../../../../hooks/usePlatforms'
import InputFormik from '../../../../components/InputFormik'

const initialValues = {
  name: '',
  email: '',
  phone_number: '',
  comments: '',
  tractor_number: '',
  trailer_number: '',
  trailer_type: '',
  carrier_id: '',
  driver_id: '',
  equipmentType: '',
  location: '',
  status: ''
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .nullable(),
  phone_number: Yup.string()
    .nullable()
    .matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid phone number')
})

const MyDriverForm = ({ driver, open, onClose, setLoad }) => {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(null)
  const dispatch = useDispatch()
  const { isTablet } = usePlatforms()
  const styles = useStyles(isTablet)

  const handleConfirmationDialog = useCallback((action) => {
    const payload = { ...confirmationDialogOpen }
    if (action) {
      dispatch(setMyDriver(payload, onClose))
    }
    setConfirmationDialogOpen(null)
  }, [dispatch, onClose, confirmationDialogOpen])

  const smallDevice = useMemo(() => Platform.OS !== 'web' || !isTablet, [isTablet])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={driver?.driver_id ? 'Edit Driver' : 'Add Driver'}
    >
      <View style={{ flex: 1 }}>
        <KeyboardView>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ width: '100%', maxWidth: 800, marginHorizontal: 'auto' }}
          >
            <Formik
              validationSchema={validationSchema}
              initialValues={driver ? formatDriver(driver) : initialValues}
              onSubmit={setConfirmationDialogOpen}
            >
              {({ values, errors, handleSubmit, touched, setFieldValue }) => (
                <>
                  <View style={styles.inputs}>
                    <View style={styles.leftInput}>
                      <LocationsSelectInput
                        name='location'
                        placeholder='Location'
                        origin
                        selected={values?.location || ''}
                        onChange={setFieldValue}
                      />
                    </View>
                    <View style={styles.rigthInput}>
                      <Select
                        name='status'
                        selected={values?.status || ''}
                        onChange={setFieldValue}
                        placeholder='Status'
                        keyProp='value'
                        nameProp='label'
                        icon='Filter'
                        options={[
                          { value: '1', label: 'Available' },
                          { value: '0', label: 'Unavailable' }
                        ]}
                      />
                    </View>
                  </View>
                  <View style={styles.inputs}>
                    <View style={styles.leftInput}>
                      <InputFormik
                        name='name'
                        onChange={setFieldValue}
                        value={values?.name || ''}
                        error={touched.name && errors.name}
                        placeholder='Name'
                        letfIcon='User'
                        testID='driverFormNameInput'
                        accessibilityLabel='driverFormNameInput'
                      />
                    </View>
                    <View style={styles.rigthInput}>
                      <InputFormik
                        name='phone_number'
                        placeholder='Phone'
                        value={values?.phone_number || ''}
                        onChange={setFieldValue}
                        error={touched.phone_number && errors.phone_number}
                        letfIcon='Phone'
                        keyboardType='phone-pad'
                        testID='driverFormPhoneInput'
                        accessibilityLabel='driverFormPhoneInput'
                      />
                    </View>
                  </View>
                  <View>
                    <InputFormik
                      name='email'
                      placeholder='Email'
                      onChange={setFieldValue}
                      error={touched.email && errors.email}
                      value={values?.email || ''}
                      letfIcon='Email'
                      keyboardType='email-address'
                      testID='driverFormEmailInput'
                      accessibilityLabel='driverFormEmailInput'
                    />
                  </View>
                  <Divider containerStyle={styles.divider} />
                  <View style={styles.inputs}>
                    <View style={styles.leftInput}>
                      <InputFormik
                        name='tractor_number'
                        placeholder='Tractor number'
                        value={values?.tractor_number || ''}
                        onChange={setFieldValue}
                        error={touched.tractor_number && errors.tractor_number}
                        letfIcon='Tractors'
                        testID='driverFormTractorNumberInput'
                        accessibilityLabel='driverFormTractorNumberInput'
                      />
                    </View>
                    <View style={styles.rigthInput}>
                      <InputFormik
                        name='trailer_number'
                        placeholder='Trailer number'
                        value={values?.trailer_number || ''}
                        onChange={setFieldValue}
                        error={touched.trailer_number && errors.trailer_number}
                        letfIcon='Tractors'
                        testID='driverFormTrailerNumberInput'
                        accessibilityLabel='driverFormTrailerNumberInput'
                      />
                    </View>
                  </View>
                  <View style={styles.inputs}>
                    <View style={styles.leftInput}>
                      <EquipmentsSelectInput
                        name='equipmentType'
                        selected={values?.equipmentType || ''}
                        onChange={setFieldValue}
                      />
                    </View>
                    <View style={styles.rigthInput}>
                      <InputFormik
                        name='comments'
                        placeholder='Comments'
                        value={values?.comments || ''}
                        onChange={setFieldValue}
                        error={touched.comments && errors.comments}
                        letfIcon='Message'
                        testID='driverFormCommentsInput'
                        accessibilityLabel='driverFormCommentsInput'
                      />
                    </View>
                  </View>
                  <View style={styles.buttonsContainer}>
                    {!smallDevice && (
                      <Button
                        title='Cancel'
                        onPress={onClose}
                        containerStyle={styles.button}
                      />
                    )}
                    <Button
                      title='Save'
                      onPress={handleSubmit}
                      loading={setLoad}
                      testID='driverFormButton'
                      accessibilityLabel='driverFormButton'
                      containerStyle={styles.button}
                    />
                  </View>
                </>
              )}
            </Formik>
          </ScrollView>
        </KeyboardView>
      </View>
      <ConfirmationDialog
        open={Boolean(confirmationDialogOpen)}
        title={`${driver?.driver_id ? 'Save' : 'Add'} Driver`}
        description='Are sure you want to save this driver?'
        onClose={handleConfirmationDialog}
      />
    </Modal>
  )
}

MyDriverForm.defaultProps = {
  driver: initialValues
}

MyDriverForm.propTypes = {
  driver: object,
  open: bool.isRequired,
  onClose: func.isRequired,
  setLoad: bool.isRequired
}

export default MyDriverForm
